import { useContext } from 'react'

import {
  ListSubheader,
  Box,
  List,
  styled,
  Button,
  ListItem,
} from '@mui/material'
import { NavLink as RouterLink } from 'react-router-dom'
import { SidebarContext } from '../../../../contexts/SidebarContext'

import SupportAgentTwoToneIcon from '@mui/icons-material/SupportAgentTwoTone'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
// import DisplaySettingsTwoToneIcon from '@mui/icons-material/DisplaySettingsTwoTone'
import { useUser } from '../../../../../../contexts/UserContext'
import { GrUserAdd, GrUserSettings } from 'react-icons/gr'
import { BiSolidCloudUpload } from 'react-icons/bi'
import {
  isAdminScope,
  isSupervisorScope,
  hasAtLeast,
} from '../../../../../../utils/role'
import BusinessIcon from '@mui/icons-material/Business'
import StorageIcon from '@mui/icons-material/Storage'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import FormatQuoteOutlinedIcon from '@mui/icons-material/FormatQuoteOutlined'
import NotificationsTwoToneIcon from '@mui/icons-material/NotificationsTwoTone'
import SmartToyTwoToneIcon from '@mui/icons-material/SmartToyTwoTone'
import TuneTwoToneIcon from '@mui/icons-material/TuneTwoTone'

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: 0 0 0 ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
)

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      padding: 8px 0;
      .MuiListItem-root {
        padding: 4px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[100]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};
          border-radius: 10px 0 0 10px;

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[100]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${theme.colors.alpha.trueWhite[100]};
            color: ${theme.colors.alpha.black[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.primary.main};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity',
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
)

function SidebarMenu() {
  const { userData } = useUser()
  const { closeSidebar } = useContext(SidebarContext)
  const role = userData?.roles

  return (
    <>
      <MenuWrapper>
        <List
          sx={{ marginTop: '3rem' }}
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Dashboards
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              {/* FB-4/FB-6: admins look for Users first, so it leads the
                  Dashboards section (renamed to Dutch 'Gebruikers'). */}
              {isAdminScope(role) && (
                <ListItem component="div" id="menu-gebruikers">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/users"
                    startIcon={<PeopleAltOutlinedIcon />}
                  >
                    Gebruikers
                  </Button>
                </ListItem>
              )}
              {isSupervisorScope(role) && (
                <ListItem component="div" id="menu-clienten">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/dashboards/clienten"
                    startIcon={<PeopleAltOutlinedIcon />}
                  >
                    Actieve cliënten
                  </Button>
                </ListItem>
              )}
              {/* A behandelaar reads the e-learning results of their own
                  clients; authoring sits under Beheer for admins. */}
              {role === 'behandelaar' && (
                <ListItem component="div" id="menu-elearning">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/elearning"
                    startIcon={<SchoolOutlinedIcon />}
                  >
                    E-learning
                  </Button>
                </ListItem>
              )}
              {/* FB-5/FB-8: the AI chat is a client feature and the client
                  surface left the web entirely — every web role gets the
                  Helpdesk contact page. */}
              <ListItem component="div" id="menu-helpdesk">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/helpdesk"
                  startIcon={<SupportAgentTwoToneIcon />}
                >
                  Helpdesk
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        {isAdminScope(role) ? (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                Beheer
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                {/* <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/instructions"
                    startIcon={<FilterVintageTwoToneIcon />}
                  >
                    Instructies
                  </Button>
                </ListItem> */}
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/elearning"
                    startIcon={<SchoolOutlinedIcon />}
                  >
                    E-learning
                  </Button>
                </ListItem>
                {/* CHL-146-008: the "Quote van de dag" list in the app. */}
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/quotes"
                    startIcon={<FormatQuoteOutlinedIcon />}
                  >
                    Quotes
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/upload-data"
                    startIcon={<BiSolidCloudUpload />}
                  >
                    Upload eigen gegevens
                  </Button>
                </ListItem>
                {hasAtLeast(role, 'superbeheerder') && (
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/organisaties"
                      startIcon={<BusinessIcon />}
                    >
                      Organisaties
                    </Button>
                  </ListItem>
                )}
                {/* CHL-073-009: Bloom decides who may read the database.
                    Superbeheerder only, like Organisaties. */}
                {hasAtLeast(role, 'superbeheerder') && (
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/databasetoegang"
                      startIcon={<StorageIcon />}
                    >
                      Databasetoegang
                    </Button>
                  </ListItem>
                )}
                {hasAtLeast(role, 'beheerder') && (
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/management/notificaties"
                      startIcon={<NotificationsTwoToneIcon />}
                    >
                      Notificaties
                    </Button>
                  </ListItem>
                )}
                {/* <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/setting"
                    startIcon={<SettingsTwoToneIcon />}
                  >
                    Instelling
                  </Button>
                </ListItem> */}
              </List>
            </SubMenuWrapper>
          </List>
        ) : (
          <></>
        )}

        {/* CHL-058-009: the superbeheerder console shows no chatbot; the
            bot trainer and its settings stay with the beheerder. */}
        {isAdminScope(role) && role !== 'superbeheerder' ? (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                Chatbot
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/bot-trainer"
                    startIcon={<SmartToyTwoToneIcon />}
                  >
                    Bot trainer
                  </Button>
                </ListItem>
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/setting"
                    startIcon={<TuneTwoToneIcon />}
                  >
                    Bot instellingen
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        ) : (
          <></>
        )}

        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Accountbeheer
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              {/* <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/details"
                  startIcon={<AccountCircleTwoToneIcon />}
                >
                  Gebruikersprofiel
                </Button>
              </ListItem> */}
              <ListItem component="div" id="menu-instellingen">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/profile/settings"
                  startIcon={<GrUserSettings />}
                >
                  Instellingen
                </Button>
              </ListItem>
              {isSupervisorScope(role) ? (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/invite"
                    startIcon={<GrUserAdd />}
                  >
                    Uitnodiging
                  </Button>
                </ListItem>
              ) : (
                <></>
              )}
            </List>
          </SubMenuWrapper>
        </List>
        {/* <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Components
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/buttons"
                  startIcon={<BallotTwoToneIcon />}
                >
                  Buttons
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/modals"
                  startIcon={<BeachAccessTwoToneIcon />}
                >
                  Modals
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/accordions"
                  startIcon={<EmojiEventsTwoToneIcon />}
                >
                  Accordions
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/tabs"
                  startIcon={<FilterVintageTwoToneIcon />}
                >
                  Tabs
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/badges"
                  startIcon={<HowToVoteTwoToneIcon />}
                >
                  Badges
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/tooltips"
                  startIcon={<LocalPharmacyTwoToneIcon />}
                >
                  Tooltips
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/avatars"
                  startIcon={<RedeemTwoToneIcon />}
                >
                  Avatars
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/cards"
                  startIcon={<SettingsTwoToneIcon />}
                >
                  Cards
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/components/forms"
                  startIcon={<TrafficTwoToneIcon />}
                >
                  Forms
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Extra Pages
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/status/404"
                  startIcon={<CheckBoxTwoToneIcon />}
                >
                  Error 404
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/status/500"
                  startIcon={<CameraFrontTwoToneIcon />}
                >
                  Error 500
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/status/coming-soon"
                  startIcon={<ChromeReaderModeTwoToneIcon />}
                >
                  Coming Soon
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/status/maintenance"
                  startIcon={<WorkspacePremiumTwoToneIcon />}
                >
                  Maintenance
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List> */}
      </MenuWrapper>
    </>
  )
}

export default SidebarMenu
